/**
 * Pure logic behind the "move project" picker: one search across every
 * pipeline the project may move to, with stages grouped by pipeline.
 * Shared by the project modal's stage panel and the pipeline card's
 * right-click menu so the two cannot drift apart again.
 */

export interface PickerStage {
  id: number;
  name: string;
}

export interface PickerPipeline {
  id: number;
  name: string;
  stages: PickerStage[];
}

export interface StageGroupRow {
  stage: PickerStage;
  /** 1-based place in the pipeline, kept while searching so a match still says where it sits. */
  position: number;
  isCurrent: boolean;
}

export interface StageGroup {
  pipeline: PickerPipeline;
  isCurrentPipeline: boolean;
  open: boolean;
  rows: StageGroupRow[];
  countLabel: string;
}

export function orderPipelinesCurrentFirst<T extends { id: number }>(
  pipelines: T[],
  currentPipelineId: number | null | undefined,
): T[] {
  const current = pipelines.find((p) => p.id === currentPipelineId);
  if (!current) return pipelines;
  return [current, ...pipelines.filter((p) => p !== current)];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function buildStageGroups({
  pipelines,
  currentPipelineId,
  currentStageId,
  query,
  expandedIds,
}: {
  pipelines: PickerPipeline[];
  currentPipelineId: number | null | undefined;
  currentStageId: number | null | undefined;
  query: string;
  expandedIds: ReadonlySet<number>;
}): StageGroup[] {
  const needle = query.trim().toLowerCase();

  return orderPipelinesCurrentFirst(pipelines, currentPipelineId).flatMap((pipeline) => {
    const isCurrentPipeline = pipeline.id === currentPipelineId;
    const rows = pipeline.stages
      .map((stage, i) => ({
        stage,
        position: i + 1,
        isCurrent: isCurrentPipeline && stage.id === currentStageId,
      }))
      .filter((row) => !needle || row.stage.name.toLowerCase().includes(needle));

    if (needle && rows.length === 0) return [];

    return [
      {
        pipeline,
        isCurrentPipeline,
        open: needle ? true : expandedIds.has(pipeline.id),
        rows,
        countLabel: needle
          ? plural(rows.length, 'match', 'matches')
          : plural(pipeline.stages.length, 'stage', 'stages'),
      },
    ];
  });
}

export function describeMoveTarget(
  pipelines: PickerPipeline[],
  pipelineId: number,
  stageId: number,
): string | null {
  const pipeline = pipelines.find((p) => p.id === pipelineId);
  const stage = pipeline?.stages.find((s) => s.id === stageId);
  return pipeline && stage ? `${pipeline.name} › ${stage.name}` : null;
}

export interface PlaceNames {
  pipeline: string;
  stage: string;
}

/** Task copy for a user who may not move projects and asks their PM to instead. */
export function buildMoveRequestCopy({
  requesterName,
  projectName,
  from,
  to,
}: {
  requesterName: string;
  projectName: string;
  from: PlaceNames;
  to: PlaceNames;
}): { title: string; description: string } {
  const crossPipeline = from.pipeline !== to.pipeline;
  const name = (p: PlaceNames) => (crossPipeline ? `${p.pipeline} › ${p.stage}` : p.stage);

  return {
    title: `${crossPipeline ? 'Pipeline' : 'Stage'} Change Request: ${projectName}`,
    description: `${requesterName} requested to move project "${projectName}" from "${name(from)}" to "${name(to)}"`,
  };
}
